import React, { useState, useEffect } from 'react';
import {
  Brain, TrendingUp, Clock, CheckCircle2,
  ExternalLink, Activity, Loader2, AlertCircle
} from 'lucide-react';

function StreamingResults({ companyData, onClose, token }) {
  const [status, setStatus] = useState('Connecting...');
  const [searchComplete, setSearchComplete] = useState(false);
  const [searchContext, setSearchContext] = useState('');
  const [website, setWebsite] = useState(null);
  // const [searchTime, setSearchTime] = useState(null);
  const [modelResults, setModelResults] = useState([]);
  const [finalResults, setFinalResults] = useState(null);
  // const [ws, setWs] = useState(null);
  const [liveConsensus, setLiveConsensus] = useState(null);

  useEffect(() => {
    // Reset all state when component mounts with new data
    setStatus('Connecting...');
    setSearchComplete(false);
    setSearchContext('');
    setWebsite(null);
    // setSearchTime(null);
    setModelResults([]);
    setFinalResults(null);
    setLiveConsensus(null);
    
    // Get WebSocket URL from API URL
    const API_URL = window.API_CONFIG?.API_URL || process.env.REACT_APP_API_URL || 
      (window.location.hostname === 'localhost' 
        ? 'http://localhost:12343' 
        : `${window.location.protocol}//${window.location.hostname.replace('era-classifier', 'era-classifier-api')}`);
    const WS_URL = API_URL.replace(/^http/, 'ws').replace(/^https/, 'wss');
    const API_KEY = process.env.REACT_APP_API_KEY || '';
    const websocket = new WebSocket(`${WS_URL}/api/v1/ws/categorise`);
    
    websocket.onopen = () => {
      setStatus('Connected. Starting categorization...');
      websocket.send(JSON.stringify({
        ...companyData,
        api_key: API_KEY,
        token: token
      }));
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'status':
          setStatus(data.message || data.data?.message || 'Processing...');
          break;
          
        case 'search_complete':
          setSearchComplete(true);
          setSearchContext(data.search_context);
          // setSearchTime(data.search_time_ms);
          break;
          
        case 'model_result':
          setModelResults(prev => {
            const newResults = [...prev, data.result];
            // Calculate live consensus
            const categoryVotes = {};
            newResults.forEach(result => {
              if (result.category !== 'Error') {
                categoryVotes[result.category] = (categoryVotes[result.category] || 0) + 1;
              }
            });
            
            // Find consensus if at least 2 models agree
            let consensus = null;
            Object.entries(categoryVotes).forEach(([category, votes]) => {
              if (votes >= 2) {
                consensus = category;
              }
            });
            setLiveConsensus(consensus);
            
            return newResults;
          });
          break;
          
        case 'complete':
          setFinalResults(data);
          setStatus('Categorization complete!');
          // Extract website if provided
          if (data.website) {
            setWebsite(data.website);
          }
          break;
          
        case 'error':
          setStatus(`Error: ${data.data?.error || data.message || 'Unknown error'}`);
          break;
          
        default:
          break;
      }
    };

    websocket.onerror = (error) => {
      setStatus('Connection error. Please try again.');
      console.error('WebSocket error:', error);
    };

    websocket.onclose = () => {
      setStatus('Connection closed.');
    };

    // setWs(websocket);

    return () => {
      websocket.close();
    };
  }, [companyData, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const getModelIcon = (model) => {
    const icons = {
      'gpt': '🤖',
      'claude': '🔮',
      'gemini': '♊',
      'mistral': '🌪️',
      'perplexity': '🔍'
    };
    
    for (const [key, icon] of Object.entries(icons)) {
      if (model.toLowerCase().includes(key)) return icon;
    }
    return '🤖';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-green-600';
    if (confidence >= 0.7) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getConfidenceWidth = (confidence) => {
    if (confidence === 0) return '5%';
    return `${Math.max(5, confidence * 100)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Status Bar */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Activity className="mr-3 text-blue-600 animate-pulse" size={24} />
            <span className="text-lg font-medium">{status}</span>
          </div>
          {finalResults && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">
              {finalResults?.corrected_company ? (
                <span className="flex items-center">
                  <span className="text-gray-500 line-through mr-2">{companyData.company}</span>
                  <span className="text-green-600">→</span>
                  <span className="ml-2">{finalResults.corrected_company}</span>
                </span>
              ) : (
                companyData.company
              )}
            </h2>
            {finalResults?.corrected_company && (
              <p className="text-sm text-green-600 mb-1">
                ✓ Company name corrected
              </p>
            )}
            <p className="text-gray-600 mb-2">{companyData.country}</p>
            
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ExternalLink className="mr-1" size={16} />
                {new URL(website).hostname}
              </a>
            )}
          </div>
          
          {finalResults && (
            <div className="text-right">
              <p className="text-sm text-gray-500">
                Total time: {finalResults.processing_time_ms}ms
              </p>
              {finalResults.search_time_ms && (
                <p className="text-xs text-gray-400">
                  Search: {finalResults.search_time_ms}ms
                </p>
              )}
            </div>
          )}
        </div>

        {!finalResults && liveConsensus && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-800 flex items-center">
              <Activity className="mr-2 animate-pulse" size={20} />
              Current Consensus: <span className="ml-2 font-bold">{liveConsensus}</span>
              <span className="ml-2 text-xs text-blue-600">(waiting for all models...)</span>
            </p>
          </div>
        )}
      </div>

      {/* Consensus Summary - Show after all models respond */}
      {finalResults && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Final Results</h3>
          
          {finalResults.consensus_category ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle2 className="mr-3 text-green-600" size={24} />
                <div>
                  <p className="text-lg font-medium text-green-800">
                    Consensus Category: <span className="font-bold">{finalResults.consensus_category}</span>
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    {finalResults.results.length === 1 
                      ? 'Single model categorization'
                      : `${finalResults.results.filter(r => r.category === finalResults.consensus_category).length} out of ${finalResults.results.length} models agree`
                    }
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="mr-3 text-yellow-600" size={24} />
                <div>
                  <p className="text-lg font-medium text-yellow-800">
                    No Consensus Reached
                  </p>
                  <p className="text-sm text-yellow-600 mt-1">
                    The models provided different categorizations. Review individual results below.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total Processing Time:</span>
              <span className="ml-2 font-medium">{finalResults.processing_time_ms}ms</span>
            </div>
          </div>
        </div>
      )}

      {/* Model Results - Live Updates */}
      {modelResults.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Brain className="mr-2" size={20} />
            AI Model Analysis ({modelResults.length} models responded)
          </h3>
          
          <div className="space-y-4">
            {modelResults.map((result, index) => (
              <div 
                key={index} 
                className="border rounded-lg p-4 hover:shadow-md transition-all duration-300 animate-fadeIn"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">{getModelIcon(result.model)}</span>
                    <div>
                      <h4 className="font-medium text-gray-800">Model {index + 1}</h4>
                      {result.company_name && (
                        <p className="text-xs text-gray-600 mt-0.5">
                          Identified: <span className="font-medium">{result.company_name}</span>
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <span className="flex items-center">
                          <Clock className="inline-block mr-1" size={12} />
                          {result.execution_time_ms}ms
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {result.category}
                  </span>
                </div>
                
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Confidence</span>
                    <span className={`text-sm font-medium ${getConfidenceColor(result.confidence)}`}>
                      {(result.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        result.confidence >= 0.9 ? 'bg-green-500' :
                        result.confidence >= 0.7 ? 'bg-yellow-500' : 'bg-orange-500'
                      }`}
                      style={{ width: getConfidenceWidth(result.confidence) }}
                    />
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 italic">
                  "{result.reasoning}"
                </p>
              </div>
            ))}
            
            {!finalResults && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="animate-spin mr-2" size={20} />
                <span className="text-gray-500">Waiting for more models...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Context */}
      {searchComplete && searchContext && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <TrendingUp className="mr-2" size={20} />
              Web Search Context
            </h3>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
              {searchContext}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default StreamingResults;